import * as React from 'react';
import { useState } from 'react';
import { useQueryClient } from 'react-query';
import { NavLink } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { 
  BarChart3, 
  MessageSquare, 
  Users, 
  Settings, 
  Database,
  Activity,
  Layers,
  Workflow,
  LogOut,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { clearAuthToken } from '@/auth/token';
import { useTenant, useTenants } from '@/hooks/useTenant';
import { getActiveTenantId, setActiveTenantId } from '@/store/tenantSelection';

interface SidebarProps {
  isCollapsed?: boolean;
}

const navigation = [
  {
    name: 'Dashboard',
    href: '/',
    icon: BarChart3,
  },
  {
    name: 'Canais',
    href: '/channels',
    icon: Layers,
  },
  {
    name: 'Fluxos',
    href: '/flows',
    icon: Workflow,
  },
  {
    name: 'Conversas',
    href: '/conversations',
    icon: MessageSquare,
  },
  {
    name: 'Contatos',
    href: '/contacts',
    icon: Users,
  },
  {
    name: 'Templates por Canal',
    href: '/templates',
    icon: Database,
  },
  {
    name: 'Analytics',
    href: '/analytics',
    icon: Activity,
  },
  {
    name: 'Configurações',
    href: '/settings',
    icon: Settings,
  },
];

export const Sidebar: React.FC<SidebarProps> = ({ isCollapsed: controlledCollapsed }) => {
  const [internalCollapsed, setInternalCollapsed] = useState(false);
  const isCollapsed = controlledCollapsed ?? internalCollapsed;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTenantId, setSelectedTenantId] = useState(getActiveTenantId());
  const { data: tenant, isLoading } = useTenant(activeTenantId);
  const { data: tenants = [] } = useTenants();

  const toggleSidebar = () => {
    setInternalCollapsed(!internalCollapsed);
  };

  const handleTenantChange = async (tenantId: string) => {
    setActiveTenantId(tenantId);
    setSelectedTenantId(tenantId);
    await queryClient.invalidateQueries();
  };

  const handleLogout = () => {
    clearAuthToken();
    navigate('/login');
  };

  return (
    <aside className={`sidebar ${isCollapsed ? 'sidebar--collapsed' : ''}`}>
      <div className="sidebar__inner">
        <div className="sidebar__header">
          <div className="sidebar__brand">
            <div className="sidebar__logo">
              <Layers className="sidebar__icon" />
            </div>
            {!isCollapsed && (
              <div className="sidebar__title">
                <strong>Central de Mensageria</strong>
                <span>WhatsApps e integrações</span>
              </div>
            )}
          </div>

          <button className="sidebar__toggle" onClick={toggleSidebar} type="button">
            {isCollapsed ? (
              <ChevronRight className="sidebar__icon" />
            ) : (
              <ChevronLeft className="sidebar__icon" />
            )}
          </button>
        </div>

        <nav className="sidebar__nav">
          {navigation.map((item) => (
            <NavLink
              key={item.name}
              to={item.href}
              className={({ isActive }) =>
                `sidebar__link ${isActive ? 'sidebar__link--active' : ''}`
              }
              title={isCollapsed ? item.name : undefined}
            >
              <item.icon className="sidebar__icon" />
              {!isCollapsed && <span className="sidebar__label">{item.name}</span>}
            </NavLink>
          ))}
        </nav>

        <div className="sidebar__footer">
          <div className="sidebar__tenant">
            <div className="sidebar__tenant-avatar">
              {isLoading ? '?' : (tenant?.name?.charAt(0) ?? tenant?.tenant_id?.charAt(0) ?? 'T')}
            </div>
            {!isCollapsed && (
              <div className="sidebar__tenant-info">
                <label className="sidebar__tenant-sub" htmlFor="active-project">Projeto ativo</label>
                <select
                  id="active-project"
                  className="sidebar__tenant-select"
                  value={activeTenantId}
                  onChange={(event) => void handleTenantChange(event.target.value)}
                >
                  {tenants.map((item) => (
                    <option key={item.tenant_id} value={item.tenant_id}>{item.name}</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <button className="sidebar__logout" type="button" onClick={handleLogout}>
            <LogOut className="sidebar__icon" />
            {!isCollapsed && <span>Sair</span>}
          </button>
        </div>
      </div>
    </aside>
  );
};
